import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { TextInput } from 'src/design/atoms/TextInput';
import { useFormik } from 'formik';
import React from 'react';
import {
  ClassificationDto,
  ClassificationRequestDto,
} from 'src/api/FiretrolApiClient';
import {
  useCreateClassification,
  useDeleteClassification,
  useUpdateClassification,
} from 'src/api/queries/organization/useClassificationMutations';
import { parse422Errors } from 'src/api/utils/errorUtils';
import { getErrorMessage, showSuccessToast } from 'src/utils';
import { InlineConfirmButton } from 'src/design/molecules/InlineConfirmButton';
import { ModalButton } from 'src/design/molecules/ModalButton';
import * as Yup from 'yup';
import { ClassificationLevelsEditor } from './ClassificationLevelsEditor';

const createValidationSchema = () =>
  Yup.object({
    name: Yup.string()
      .required('Name is required')
      .min(1, 'Name must be at least 1 character'),
    levels: Yup.array().of(
      Yup.object({
        name: Yup.string()
          .required('Level name is required')
          .min(1, 'Level name must be at least 1 character'),
        rateModifierPercent: Yup.number()
          .required('Rate modifier percent is required')
          .min(0, 'Rate modifier must be at least 0')
          .max(1000, 'Rate modifier must be at most 1000'),
      }),
    ),
  });

const initialValues: ClassificationRequestDto = {
  name: '',
  levels: [],
};

export interface IAddClassificationFormProps {
  selectedClassification?: ClassificationDto;
  setAddClassificationOpen: (
    isOpen: boolean,
    classification?: ClassificationDto,
  ) => void;
}

export const AddClassificationForm: React.FC<IAddClassificationFormProps> = ({
  selectedClassification,
  setAddClassificationOpen,
}) => {
  const [error, setError] = React.useState<string | null>(null);
  const [validationMessages, setValidationMessages] = React.useState<string[]>(
    [],
  );
  const [isAddingNewLevel, setIsAddingNewLevel] = React.useState(false);
  const [levelsEditorResetKey, setLevelsEditorResetKey] = React.useState(0);
  const createClassification = useCreateClassification();
  const updateClassification = useUpdateClassification();
  const deleteClassification_ = useDeleteClassification();

  const closeDialog = () => {
    setAddClassificationOpen(false);
    formik.resetForm();
    setValidationMessages([]);
    setIsAddingNewLevel(false);
    setLevelsEditorResetKey((key) => key + 1);
  };

  const deleteClassification = async () => {
    if (!selectedClassification) return;
    setError(null);
    try {
      await deleteClassification_.mutateAsync(selectedClassification.id);
      showSuccessToast(`${selectedClassification.name} deleted successfully!`);
      closeDialog();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to delete classification'));
    }
  };

  const formik = useFormik({
    initialValues: selectedClassification
      ? {
          id: selectedClassification.id,
          name: selectedClassification.name,
          levels: selectedClassification.levels || [],
        }
      : initialValues,
    validationSchema: createValidationSchema(),
    validateOnMount: true,
    onSubmit: async (values) => {
      setError(null);
      setValidationMessages([]);

      try {
        const payload: ClassificationRequestDto = {
          id: selectedClassification?.id,
          name: values.name,
          levels: values.levels?.map((level) => ({
            id: level.id,
            name: level.name,
            rateModifierPercent: level.rateModifierPercent,
          })),
        };

        if (selectedClassification) {
          await updateClassification.mutateAsync({
            id: selectedClassification.id,
            request: payload,
          });
          showSuccessToast(`${values.name} updated successfully!`);
        } else {
          await createClassification.mutateAsync(payload);
          showSuccessToast(`${values.name} created successfully!`);
        }
        closeDialog();
      } catch (err: unknown) {
        const validationErrors = parse422Errors(err);
        if (validationErrors?.messages?.length > 0) {
          setValidationMessages(validationErrors.messages);
        } else {
          setError(getErrorMessage(err, 'An error occurred'));
        }
      }
    },
  });

  const levelsError =
    formik.touched.levels &&
    formik.errors.levels &&
    typeof formik.errors.levels === 'string'
      ? formik.errors.levels
      : undefined;

  const isSubmitDisabled = !formik.isValid || isAddingNewLevel;

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack>
        {error && (
          <Alert color="red" title="Error">
            {error}
          </Alert>
        )}
        {validationMessages.length > 0 && (
          <Alert color="red" title="Validation Errors">
            {validationMessages.map((msg, idx) => (
              <div key={idx}>{msg}</div>
            ))}
          </Alert>
        )}

        <TextInput
          label="Classification Name"
          placeholder="Enter classification name"
          {...formik.getFieldProps('name')}
          error={formik.touched.name && (formik.errors.name as string)}
          description="The title or classification name (e.g., Supervisor, Foreman, Laborer)"
          withAsterisk
        />

        <ClassificationLevelsEditor
          key={levelsEditorResetKey}
          levels={formik.values.levels || []}
          onChange={(levels) => formik.setFieldValue('levels', levels)}
          error={levelsError}
          onAddingChange={setIsAddingNewLevel}
        />

        <Group justify="space-between" mt="md">
          <Group>
            {selectedClassification && (
              <InlineConfirmButton
                loading={deleteClassification_.isPending}
                color="red"
                onConfirmClick={deleteClassification}
              >
                Delete Classification
              </InlineConfirmButton>
            )}
          </Group>
          <Group>
            <ModalButton onClick={closeDialog} variant="subtle">
              Cancel
            </ModalButton>
            <ModalButton
              type="submit"
              disabled={isSubmitDisabled}
              loading={formik.isSubmitting}
            >
              {selectedClassification ? 'Update' : 'Create'}
            </ModalButton>
          </Group>
        </Group>
      </Stack>
    </form>
  );
};
